/**
 * File download utilities.
 *
 * Single download: Uses presigned URLs with Content-Disposition: attachment header.
 * The browser navigates to the URL natively — no fetch(), no CORS, no memory issues.
 *
 * Batch download: Fetches files via presigned download URLs and packs into a ZIP.
 * Requires S3 CORS to be configured for the frontend's origin.
 */
import JSZip from 'jszip'

/**
 * Download a single file by navigating to its download URL.
 * The URL must include Content-Disposition: attachment (S3 ResponseContentDisposition).
 * This works for any file size and doesn't require CORS.
 */
export function downloadFile(downloadUrl: string): void {
  const a = document.createElement('a')
  a.href = downloadUrl
  a.download = ''  // Browser will use Content-Disposition filename
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

/**
 * Download multiple files as a single ZIP archive.
 * Files are fetched in parallel with a concurrency limit.
 * Uses Promise.allSettled so individual failures don't abort the entire batch.
 *
 * Note: This uses fetch() on presigned URLs, which requires S3 CORS configuration.
 */
export async function downloadFilesAsZip(
  files: Array<{ url: string; filename: string }>,
  zipFilename: string,
  onProgress?: (completed: number, total: number) => void,
): Promise<{ succeeded: number; failed: number }> {
  const zip = new JSZip()
  let completed = 0
  let succeeded = 0
  let failed = 0

  // Deduplicate filenames
  const usedNames = new Set<string>()
  const deduped = files.map((f) => {
    let name = f.filename
    if (usedNames.has(name)) {
      const dot = name.lastIndexOf('.')
      const base = dot > 0 ? name.slice(0, dot) : name
      const ext = dot > 0 ? name.slice(dot) : ''
      let i = 2
      while (usedNames.has(`${base}-${i}${ext}`)) i++
      name = `${base}-${i}${ext}`
    }
    usedNames.add(name)
    return { ...f, filename: name }
  })

  // Download with concurrency limit
  const CONCURRENCY = 4
  for (let i = 0; i < deduped.length; i += CONCURRENCY) {
    const chunk = deduped.slice(i, i + CONCURRENCY)
    const results = await Promise.allSettled(
      chunk.map(async (file) => {
        const response = await fetch(file.url)
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        const blob = await response.blob()
        return { filename: file.filename, blob }
      }),
    )

    for (const result of results) {
      completed++
      if (result.status === 'fulfilled') {
        zip.file(result.value.filename, result.value.blob)
        succeeded++
      } else {
        failed++
      }
      onProgress?.(completed, deduped.length)
    }
  }

  if (succeeded === 0) {
    throw new Error('All downloads failed')
  }

  const zipBlob = await zip.generateAsync({ type: 'blob' })
  const objectUrl = URL.createObjectURL(zipBlob)
  const a = document.createElement('a')
  a.href = objectUrl
  a.download = zipFilename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(objectUrl)

  return { succeeded, failed }
}
